import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel as MuiInputLabel,
  MenuItem,
  OutlinedInput as MuiOutlinedInput,
  OutlinedInputProps,
} from "@mui/material";
import React, { forwardRef, useRef, useState } from "react";
import { IMaskInput } from "react-imask";
import useTheme from "../../../../common/hooks/useTheme";
import styled from "styled-components";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { inToFtAndIn, Round } from "../../../questionnaire/utils";
import { useDispatch, useSelector } from "react-redux";
import { RootStateStepper } from "../../../questionnaire/store/stepper-slice";
import {
  answersActions,
  RootStateAnswers,
} from "../../../questionnaire/store/answers-slice";
import { biometricInfoType, personalInfoType } from "../../store/helper";
import {
  MAX_HEIGHT_CM,
  MAX_HEIGHT_FT,
  MAX_HEIGHT_IN,
  MAX_WEIGHT_KG,
  MAX_WEIGHT_LB,
  MIN_HEIGHT_CM,
  MIN_WEIGHT_LB,
  MIN_WEIGHT_KG,
  MIN_HEIGHT_IN,
} from "../../../questionnaire/constant";
import {
  validateEmail,
  validateText,
  validateZipCode,
} from "../../../../providers/helperFunctions";
import {
  DayListItemType,
  FoodListItemType,
  ListItemType,
} from "../nutritionTab/type";
import LikedSvg from "../../../../common/svgs/likedSvg";
import DislikedSvg from "../../../../common/svgs/dislikedSvg";
import CustomPopover from "../common/CustomPopover";
import { Theme } from "../../../../providers/ThemeProvider";
import UpdatedBadge from "./UpdatedBadge";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

// After
interface StyledOutlinedInputProps extends OutlinedInputProps {
  $has_error?: boolean;
  $is_numeric?: boolean;
}

const PhoneMaskCustom = forwardRef<HTMLInputElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="000-000-0000"
        definitions={{
          "#": /[1-9]/,
        }}
        inputRef={ref}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

// const ZipCodeMaskCustom = forwardRef<HTMLInputElement, CustomProps>(
//   function TextMaskCustom(props, ref) {
//     const { onChange, ...other } = props;
//     return (
//       <IMaskInput
//         {...other}
//         mask="00000" // Mask for 5-digit ZIP code
//         definitions={{
//           "0": /[0-9]/, // Define digit
//         }}
//         inputRef={ref}
//         onAccept={(value: any) =>
//           onChange({ target: { name: props.name, value } })
//         }
//         overwrite
//       />
//     );
//   }
// );

// Styled OutlinedInput
const StyledOutlinedInput = styled(MuiOutlinedInput)<StyledOutlinedInputProps>(
  ({ theme, $has_error, $is_numeric }) => ({
    height: "56px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "8px",

    "& .MuiOutlinedInput-input": {
      padding: "24px 0 8px 12px",

      display: "flex",
      alignItems: "center",
      fontFamily: "var(--font-Medium)",
      fontSize: "12px",
      width: $is_numeric ? "7%" : "",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
      borderColor: $has_error
        ? theme.colors_ds.DANGER_GRAPH_DEFICIT
        : theme.colors_ds.NEUTRAL_BORDER_WEAK,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: $has_error
        ? theme.colors_ds.DANGER_GRAPH_DEFICIT
        : theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: $has_error
        ? theme.colors_ds.DANGER_GRAPH_DEFICIT
        : theme.colors_ds.PRIMARY_ICON_MAIN,
    },
    legend: {
      display: "none",
    },
    fieldset: {
      top: 0,
    },
  })
);

const StyledSelect = styled(Box)(({ theme }) => ({
  height: "56px",
  borderRadius: "8px",
  padding: "8px 4px 8px 12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontFamily: "var(--font-Medium)",
  fontSize: "12px",
}));

// Styled MenuItem
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  minWidth: "200px",
  borderRadius: "8px",
  padding: "20px 12px",
  display: "flex",
  fontFamily: "var(--font-Medium)",
  fontSize: "12px",
  justifyContent: "space-between",
  border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  "&:hover": {
    backgroundColor: "transparent", // Remove hover background
    borderColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
  },
  "&.Mui-selected": {
    backgroundColor: "transparent", // Remove selected background
    borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
  },
  "&.Mui-selected:hover": {
    backgroundColor: "transparent", // Remove hover on selected item
    borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
  },
}));

const StyledInputLabel = styled(MuiInputLabel)(({ theme }) => ({
  fontFamily: "var(--font-Medium)",
  fontSize: "10px",
  backgroundColor: "transparent",
  transition: "transform 0.2s ease-in-out",
  color: theme.colors_ds.NEUTRAL_TEXT_WEAK,

  "&.Mui-focused": {
    color: theme.colors_ds.PRIMARY_ICON_MAIN,
  },
}));

const StyledFormHelperText = styled(FormHelperText)`
  font-family: var(--font-Medium), sans-serif;
  font-size: 10px;
  position: absolute;
  bottom: -18px;
  left: 0;
  background-color: transparent;
  color: ${(props) => props.theme.colors_ds.DANGER_GRAPH_DEFICIT};
`;

const CustomInputField = ({
  type,
  category,
  openPopperId,
  setOpenPopperId,
}: {
  type: string;
  category: string;
  openPopperId: string | null;
  setOpenPopperId: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const curState = useSelector(
    (state: RootStateStepper) => state.stepper.formData
  );

  const answers = useSelector((state: RootStateAnswers) => state.answers);

  const item = curState.find((el: any) => el.category === category);

  const measurementUnit =
    answers.biometricQuest.myAnswers.mass_measurement_unit;

  const max =
    category === biometricInfoType.current_weight ||
    category === biometricInfoType.target_weight
      ? measurementUnit === 1
        ? MAX_WEIGHT_KG
        : MAX_WEIGHT_LB
      : measurementUnit === 1
      ? MAX_HEIGHT_CM
      : MAX_HEIGHT_FT * 9 + MAX_HEIGHT_IN;

  const min =
    category === biometricInfoType.current_weight ||
    category === biometricInfoType.target_weight
      ? measurementUnit === 1
        ? MIN_WEIGHT_KG
        : MIN_WEIGHT_LB
      : measurementUnit === 1
      ? MIN_HEIGHT_CM
      : MIN_HEIGHT_IN;

  const handleLabel = (cat: string) => {
    if (cat === personalInfoType.firstName) {
      return "First Name";
    }
    if (cat === personalInfoType.lastName) {
      return "Last name";
    }
    if (cat === personalInfoType.email) {
      return "Email account";
    }
    if (cat === personalInfoType.phone) {
      return "Phone number";
    }
    if (cat === personalInfoType.countryID) {
      return "Country";
    }
    if (cat === personalInfoType.city) {
      return "City";
    }
    if (cat === personalInfoType.address) {
      return "Address";
    }
    if (cat === personalInfoType.zip) {
      return "Zip code";
    }
    if (cat === biometricInfoType.height && measurementUnit === 1) {
      return `${item.label}, cm`;
    }
    if (cat === biometricInfoType.height && measurementUnit === 2) {
      return `${item.label}, ft`;
    }
    if (
      (cat === biometricInfoType.current_weight ||
        cat === biometricInfoType.target_weight) &&
      measurementUnit === 1
    ) {
      return `${item.label}, kg`;
    }
    if (
      (cat === biometricInfoType.current_weight ||
        cat === biometricInfoType.target_weight) &&
      measurementUnit === 2
    ) {
      return `${item.label}, lb`;
    }
    return item.label;
  };

  const handleUnit = (cat: string) => {
    if (
      cat === biometricInfoType.current_weight ||
      cat === biometricInfoType.target_weight
    ) {
      return measurementUnit === 1 ? "kg" : "lb";
    }

    if (cat === biometricInfoType.height && measurementUnit === 1) {
      return "cm";
    }
    if (cat === biometricInfoType.height && measurementUnit === 2) {
      return `ft,in`;
    }
  };

  const handleValue = (cat: string) => {
    if (
      cat === biometricInfoType.current_weight ||
      cat === biometricInfoType.target_weight
    ) {
      return Math.round(answers.biometricQuest.myAnswers[cat]);
    }

    if (cat === biometricInfoType.height && measurementUnit === 1) {
      return Math.round(answers.biometricQuest.myAnswers[cat]);
    }
    if (cat === biometricInfoType.height && measurementUnit === 2) {
      const imperialValues = inToFtAndIn(
        Math.round(answers.biometricQuest.myAnswers[cat])
      );
      return `${imperialValues.value1} ${imperialValues.value2}`;
    }

    if (
      cat === personalInfoType.firstName ||
      cat === personalInfoType.email ||
      cat === personalInfoType.phone ||
      cat === personalInfoType.address ||
      cat === personalInfoType.zip ||
      cat === personalInfoType.city ||
      cat === personalInfoType.lastName
    ) {
      return answers.personalInfoAnswers[cat] ?? "";
    }

    if (
      item.choices.find(
        (el: any) => el.index === answers.biometricQuest.myAnswers[cat]
      )?.label === undefined
    ) {
      return type === "select" ? "" : "---";
    }

    return item.choices.find(
      (el: any) => el.index === answers.biometricQuest.myAnswers[cat]
    )?.index;
  };
  const handleChange = async (event: any) => {
    let newValue = event.target.value;

    // Validate based on the type of input
    if (type === "email" && !validateEmail(newValue as string)) {
      setError("Invalid email format");
    } else if (type === "string" && !validateText(newValue as string)) {
      setError("Required field");
    } else {
      setError(""); // Clear error if valid
    }
    if (type === "numeric" && (newValue > max || isNaN(newValue))) {
      return;
    }

    if (biometricInfoType[category as keyof typeof biometricInfoType]) {
      if (type === "numeric") {
        newValue = +newValue;
      }
      dispatch(
        answersActions.updateBiometricAnswers({
          [category]: newValue,
        })
      );
      if (newValue === answers.biometricOldAnswers[category]) {
        if (answers.biometric_modified_fields.indexOf(category) !== -1) {
          dispatch(answersActions.removeBiometricModifiedFields(category));
        }
      } else {
        if (answers.biometric_modified_fields.indexOf(category) === -1) {
          dispatch(answersActions.addBiometricModifiedFields(category));
        }
      }
    } else {
      dispatch(
        answersActions.updatePersonalInfoAnswers({
          [category]: newValue,
        })
      );

      if (newValue === answers.personalInfoInitialAnswers[category]) {
        if (answers.personalInfoModifiedFields.indexOf(category) !== -1) {
          dispatch(answersActions.removePersonalInfoModifiedFields(category));
        }
      } else {
        if (answers.personalInfoModifiedFields.indexOf(category) === -1) {
          dispatch(answersActions.addPersonalInfoModifiedFields(category));
        }
      }
    }
  };

  const handleChangeNumeric = (changeValue: number) => {
    inputRef.current?.focus();
    let newValue;
    if (changeValue === 1) {
      newValue = Math.min(
        answers.biometricQuest.myAnswers[category] + changeValue,
        max
      );
    } else {
      newValue = Math.max(
        answers.biometricQuest.myAnswers[category] + changeValue,
        min
      );
    }

    if (newValue === answers.biometricOldAnswers[category]) {
      if (answers.biometric_modified_fields.indexOf(category) !== -1) {
        dispatch(answersActions.removeBiometricModifiedFields(category));
      }
    } else {
      if (answers.biometric_modified_fields.indexOf(category) === -1) {
        dispatch(answersActions.addBiometricModifiedFields(category));
      }
    }

    dispatch(
      answersActions.updateBiometricAnswers({
        [category]: newValue,
      })
    );

    if (
      category === biometricInfoType.current_weight &&
      Round(answers.biometricQuest.myAnswers.target_weight) === newValue
    ) {
      dispatch(
        answersActions.updateBiometricAnswers({
          fat_loss_intensity: 0,
        })
      );
    }

    if (
      category === biometricInfoType.target_weight &&
      Round(answers.biometricQuest.myAnswers.current_weight) === newValue
    ) {
      dispatch(
        answersActions.updateBiometricAnswers({
          fat_loss_intensity: 0,
        })
      );
    }
  };

  let availableChoices;

  if (type === "select") {
    availableChoices = item.choices;
  }

  const curLabel = handleLabel(category);

  // "Updated" marks a field carrying an edit the client has submitted but the
  // affiliate has not yet approved (hydrated from the questionnaire's pending
  // `edits`), and is unrelated to unsaved local changes.
  const isUpdated =
    [
      ...answers.biometric_modified_fields,
      ...answers.personalInfoModifiedFields,
    ].indexOf(category) !== -1;

  const handleOpenCustomMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (setOpenPopperId === undefined) return;
    if (
      category === biometricInfoType.fat_loss_intensity &&
      answers.biometricQuest.myAnswers.current_weight ===
        answers.biometricQuest.myAnswers.target_weight
    ) {
      return;
    }

    if (openPopperId === curLabel) {
      setOpenPopperId(null);
    } else {
      setOpenPopperId(curLabel);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        minWidth: category === personalInfoType.countryCode ? "80px" : "",
        flex: category === personalInfoType.phone ? 1 : 0,
        position: "relative",
      }}
    >
      {type === "select" && (
        <Box sx={{ position: "relative" }}>
          <StyledSelect
            theme={theme}
            onClick={handleOpenCustomMenu}
            sx={{
              border: `1px solid ${
                openPopperId === curLabel
                  ? theme.colors_ds.PRIMARY_TEXT_MAIN
                  : theme.colors_ds.NEUTRAL_BORDER_WEAK
              }`,
              backgroundColor:
                category === biometricInfoType.fat_loss_intensity &&
                answers.biometricQuest.myAnswers.current_weight ===
                  answers.biometricQuest.myAnswers.target_weight
                  ? theme.colorTokens.N[20]
                  : theme.colors_ds.NEUTRAL_GENERAL_WHITE,
            }}
          >
            <Box>
              <div style={styles.label(theme, openPopperId === curLabel)}>
                {curLabel}
                {isUpdated && <UpdatedBadge />}
              </div>
              <Box>
                {item.choices.find(
                  (option: any) => option.index === handleValue(category)
                )?.label}
              </Box>
            </Box>
            <Box style={styles.button(openPopperId === curLabel)}>
              <KeyboardArrowDownIcon />
            </Box>
          </StyledSelect>
          <CustomPopover open={openPopperId === curLabel}>
            <Box
              sx={{
                border: 1,
                my: 1,
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                borderRadius: "8px",
                bgcolor: theme.colors_ds.NEUTRAL_BG_MAIN,
                boxShadow: "var(--box-shadow-general)",
                borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
                maxHeight: "300px",
                overflowX: "hidden",
                overflowY: "auto",
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
                  borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
                },
              }}
            >
              {availableChoices.map((option: any) => (
                <StyledMenuItem
                  key={option.index}
                  value={option.index}
                  theme={theme}
                  onClick={() => {
                    handleChange({
                      target: { name: category, value: option.index },
                    });
                    setOpenPopperId(null);
                  }}
                >
                  {option.label}
                  {handleValue(category) === option.index ? (
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
                        border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="10"
                        viewBox="0 0 12 10"
                        fill="none"
                      >
                        <path
                          d="M10.725 0.725L3.75 7.7L1.275 5.225C0.975 4.925 0.525 4.925 0.225 5.225C-0.075 5.525 -0.075 5.975 0.225 6.275L3.225 9.275C3.375 9.425 3.525 9.5 3.75 9.5C3.975 9.5 4.125 9.425 4.275 9.275L11.775 1.775C12.075 1.475 12.075 1.025 11.775 0.725C11.475 0.425 11.025 0.425 10.725 0.725Z"
                          fill={theme.colors_ds.NEUTRAL_BG_MAIN}
                        />
                      </svg>
                    </div>
                  ) : (
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  )}
                </StyledMenuItem>
              ))}
            </Box>
          </CustomPopover>
        </Box>
      )}
      {type !== "select" && (
        <FormControl
          variant="outlined"
          fullWidth
          sx={{
            position: "relative",
          }}
        >
          <StyledInputLabel
            htmlFor={`${category}-input`}
            theme={theme}
            style={{
              transform:
                handleValue(category) !== "" || focused
                  ? "translate(12px, 10px) scale(1)"
                  : "translate(12px, 20px) scale(1)",
            }}
          >
            {handleLabel(category)}
            {isUpdated && <UpdatedBadge />}
          </StyledInputLabel>
          <StyledOutlinedInput
            type={
              type === "double-numeric" || type === "numeric"
                ? "numeric"
                : "text"
            }
            value={handleValue(category)}
            onFocus={() => {
              setFocused(true);
              setOpenPopperId(null);
            }}
            onBlur={() => setFocused(false)}
            id={`${category}-input`}
            // readOnly={type === "double-numeric" || type === "numeric"}
            onChange={handleChange}
            inputRef={inputRef}
            inputComponent={
              type === "phone"
                ? (PhoneMaskCustom as any)
                : // : type === "zipcode"
                  // ? ZipCodeMaskCustom
                  undefined
            }
            theme={theme}
            $has_error={error !== ""}
            $is_numeric={type === "double-numeric" || type === "numeric"}
            endAdornment={
              type === "double-numeric" || type === "numeric" ? (
                <InputAdornment position="end" sx={{ margin: "0" }}>
                  <span
                    style={{
                      fontFamily: "var(--font-Medium)",
                      fontSize: "12px",
                      color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
                      paddingTop: "16px",
                    }}
                  >
                    {handleUnit(category)}
                  </span>
                </InputAdornment>
              ) : null
            } // Added u
          />
          {(type === "numeric" || type === "double-numeric") && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                // marginLeft: "-28px",
                position: "absolute",
                top: "50%",
                right: "4px",
                transform: "translateY(-50%)",
                zIndex: 50,
              }}
            >
              <IconButton
                aria-label="increase"
                onClick={() => handleChangeNumeric(1)}
                disabled={answers.biometricQuest.myAnswers[category] >= max}
                sx={{
                  padding: "0",
                  width: "21px",
                  height: "21px",
                  color: focused
                    ? theme.colors_ds.PRIMARY_ICON_MAIN
                    : theme.colors_ds.NEUTRAL_TEXT_WEAK,
                }}
              >
                <KeyboardArrowUpIcon
                  sx={{ fontSize: "21px", color: "inherit" }}
                />
              </IconButton>
              <IconButton
                aria-label="decrease"
                onClick={() => handleChangeNumeric(-1)}
                disabled={answers.biometricQuest.myAnswers[category] <= min}
                sx={{
                  padding: "0",
                  width: "21px",
                  height: "21px",
                  color: focused
                    ? theme.colors_ds.PRIMARY_ICON_MAIN
                    : theme.colors_ds.NEUTRAL_TEXT_WEAK,
                }}
              >
                <KeyboardArrowDownIcon
                  sx={{ fontSize: "21px", color: "inherit" }}
                />
              </IconButton>
            </Box>
          )}
        </FormControl>
      )}
      {error !== "" && (
        <StyledFormHelperText theme={theme}>{error}</StyledFormHelperText>
      )}
    </Box>
  );
};

export default CustomInputField;

const styles = {
  button: (open: boolean) => ({
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0",
    height: "16px",
    outline: "none",
    transition: "transform 0.3s ease",
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
  }),
  label: (theme: Theme, open: boolean) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    backgroundColor: "transparent",
    transition: "transform 0.2s ease-in-out",
    color: open
      ? theme.colors_ds.PRIMARY_TEXT_MAIN
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
